import { observer } from 'mobx-react'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import Spinner from '../components/Spinner'
import { useParams, useSearchParams } from 'react-router-dom'
import BreadCrumbs from '../components/Breadcrumbs'
import { Container } from '../components/Animations'
import Radom, { Customer, ExternalAccount, Payout as IPayout, ManagedBalanceLedger } from '../state/Radom'
import { StatusBadge } from './CheckoutSessions'
import _ from 'lodash'
import {
  errorToast,
  formatAssetAmount,
  shortAddress,
  successToast,
  unifyLedger,
  useTestMode,
  ROUTES
} from '../util/Util'
import { RADOM_COLORS } from '../util/Constants'
import TimeText from '../components/TimeText'
import { IconButton, SecondaryLinkButton } from '../components/Button'
import Copy from '../icons/Copy'
import { ExternalLinkText } from './Events'
import { formatExplorerUrl } from '../util/Chains'
import LinkExternal from '../components/LinkExternal'
import { getMethod, getMethodLogoByTicker, ManagedPaymentMethod, prodNetworks, testNetworks } from '../util/Managed'
import { Section, SectionContent, SectionItem, SubTitle } from '../components/Shared'
import Modal from '../components/Modal'
import ExportPayoutEntriesForm from '../components/ExportPayoutEntriesForm'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import Gate from '../components/Gate'
import { usePermissions } from '../util/permissions'
import HedgedPayoutCostBreakdown from './Funds/PayoutsComponents/HedgedPayoutCostBreakdown'
import {
  linkExternalIconStyle,
  loadingWrapperStyle,
  containerStyle,
  headerRowStyle,
  dropdownContentStyle,
  payoutHeaderStyle,
  payoutLogoStyle,
  tickerBadgeStyle,
  feeRowStyle,
  feeLogoStyle,
  ledgerTitleStyle,
  ledgerRowStyle,
  ledgerMutedTextStyle,
  payoutAddressCellStyle,
  iconButtonNoPaddingStyle,
  copyIconStyle,
  txLinkStyle
} from './PayoutStyles'
import { payoutBadgeStatus, payoutStatusLabel } from '../util/PayoutStatus'
import { STAGE_EXPECTATIONS } from './Transactions'
import Table from '../components/Table'
import { networkName } from '../util/AssetLabels'
import { ledgerBalanceLabel } from '../util/Ledger'

/** The balance a ledger row is for: "USDC on Polygon", or "USDC · All networks" for a pooled balance. */
const Payout = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const { has, isLoaded } = usePermissions()

  const [payout, setPayout] = useState<IPayout>()
  const [isLoading, setIsLoading] = useState(true)
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger[]>()
  const [externalAccounts, setExternalAccounts] = useState<Map<string, ExternalAccount>>(new Map())
  const [exportPayoutEntriesModalOpen, setExportPayoutEntriesModalOpen] = useState(false)
  const [testMode] = useTestMode()

  const copyToClipboard = useCallback(
    async (text: string, successMsg: string, failureMsg: string) => {
      try {
        await navigator.clipboard.writeText(text)
        successToast(successMsg)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('copy failed', err)
        errorToast(failureMsg)
      }
    },
    []
  )

  const isFiatPayout = useMemo(() => {
    return payout ? (payout.fiatEntries && payout.fiatEntries.length > 0) : false
  }, [payout])

  const canReadEvents = has('ReadEvents')
  const canReadCustomers = has('ReadCustomers')
  const canReadExternalAccounts = has('ReadExternalAccount')

  const fetchPayoutData = useCallback(
    async (id: string): Promise<void> => {
      setIsLoading(true)

      let payoutResp: IPayout
      try {
        payoutResp = await Radom.getPayout(id)
        setPayout(payoutResp)

        // Set payment method from crypto entries if available
        if (payoutResp.cryptoEntries && payoutResp.cryptoEntries.length > 0) {
          const firstCrypto = payoutResp.cryptoEntries[0]
          setPaymentMethod(getMethod(firstCrypto.network, firstCrypto.token))
        }
      } catch {
        setIsLoading(false)
        errorToast('Failed to load payout')
        return
      }

      if (canReadEvents) {
        try {
          // The funding debit books under the funding withdrawal, not the payout id,
          // and hedged payouts traverse transit balances the merchant never sees:
          // fetch both sets of rows, then keep only the funding asset's.
          const ledger = await Radom.listManagedBalanceLedger(
            [payoutResp.id, ...(payoutResp.fundingWithdrawalIds ?? []), ...(payoutResp.fundingConversionIds ?? [])]
          )
          const fundingTicker = payoutResp.fundingCurrency ||
            payoutResp.cryptoEntries?.[0]?.fundingCurrency ||
            ((payoutResp.fiatEntries?.length ?? 0) > 0 ? 'USDC' : undefined)
          const relevant = fundingTicker
            ? ledger.filter(item => getMethod(item.network, item.token).ticker === fundingTicker)
            : ledger
          if (relevant.length === 1) {
            setManagedBalanceLedger(relevant)
          } else {
            setManagedBalanceLedger(unifyLedger(relevant))
          }
        } catch {
          errorToast('Failed to load ledger')
        }
      }

      // Load customers for crypto entries
      const ids = Array.from(
        new Set(
          [
            ...(payoutResp.cryptoEntries || []).map(e => e.customerId),
            ...(payoutResp.fiatEntries || []).map(e => e.customerId)
          ].filter((id): id is string => Boolean(id))
        )
      )

      // Beneficiary names come from paging GET /customers, which needs ReadCustomers.
      // Without it the entry tables just render '-' in the beneficiary column.
      if (canReadCustomers) {
        const needed = new Set(ids)
        const found: Customer[] = []
        const limit = 20
        const parallelQueries = 3
        const networks = testMode ? testNetworks : prodNetworks
        const sortType = 'createdAtDescending'

        try {
          const firstPage = await Radom.listCustomers(0, limit, networks, sortType)
          const total = firstPage.total

          for (const c of firstPage.data) {
            if (needed.has(c.id)) {
              found.push(c)
              needed.delete(c.id)
              if (needed.size === 0) break
            }
          }

          if (total >= limit && needed.size > 0) {
            for (
              let baseOffset = limit;
              baseOffset < total && needed.size > 0;
              baseOffset += limit * parallelQueries
            ) {
              const offsets: number[] = []
              for (let k = 0; k < parallelQueries; k++) {
                const off = baseOffset + k * limit
                if (off < total) offsets.push(off)
              }

              const pages = await Promise.all(
                offsets.map(async off => await Radom.listCustomers(off, limit, networks, sortType))
              )

              for (const page of pages) {
                for (const c of page.data) {
                  if (needed.has(c.id)) {
                    found.push(c)
                    needed.delete(c.id)
                    if (needed.size === 0) break
                  }
                }
                if (needed.size === 0) break
              }
            }
          }

          setCustomers(found)
        } catch {
          errorToast('Failed to load contacts')
        }
      }

      const externalAccountIds = Array.from(
        new Set(
          [
            ...(payoutResp.fiatEntries || []).map(e => e.externalAccountId),
            ...(payoutResp.cryptoEntries || []).map(e => e.externalAccountId)
          ].filter((id): id is string => Boolean(id))
        )
      )
      // The "Account Details" column is hydrated from GET /external_account/:id (ReadExternalAccount).
      // Without it the column falls back to '-'; the External Account ID column still renders.
      if (canReadExternalAccounts && externalAccountIds.length > 0) {
        try {
          const accounts = await Promise.all(
            externalAccountIds.map(async id => await Radom.getExternalAccount(id))
          )
          const accountMap = new Map<string, ExternalAccount>()
          for (const acct of accounts) {
            accountMap.set(acct.id, acct)
          }
          setExternalAccounts(accountMap)
        } catch {
          errorToast('Failed to load external accounts')
        }
      }

      setIsLoading(false)
    },
    [testMode, canReadEvents, canReadCustomers, canReadExternalAccounts]
  )

  const debouncedFetchPayoutData = useMemo(() => _.debounce(fetchPayoutData, 300), [fetchPayoutData])

  const handleCloseExportPayoutEntriesModal = useCallback(() => {
    setExportPayoutEntriesModalOpen(false)
  }, [])

  const handleOpenExportPayoutEntriesModal = useCallback(() => {
    setExportPayoutEntriesModalOpen(true)
  }, [])

  useEffect(() => {
    if (!params.id) return
    // Hold until the permission list is in: `has` fails open on a fresh page load, so an
    // early run would fetch the ledger, customers and external accounts despite the guards.
    if (!isLoaded) return
    debouncedFetchPayoutData(params.id)
    return () => debouncedFetchPayoutData.cancel()
  }, [params.id, debouncedFetchPayoutData, isLoaded])

  if (isLoading) {
    return (
      <div style={loadingWrapperStyle}>
        <Spinner />
      </div>
    )
  }

  if (payout) {
    return (
      <Container style={containerStyle}>
        <div style={headerRowStyle}>
          <BreadCrumbs
            items={
              source === 'transactions'
                ? [
                  { item: 'Transactions', to: '/transactions' },
                  { item: payout.id, to: `${ROUTES.getPayoutDetailRoute(payout.id)}?source=transactions` }
                ]
                : [
                  { item: 'Payouts', to: ROUTES.PAYOUTS },
                  { item: payout.id, to: '.' }
                ]
            }
          />

          <Gate permission='ReadEvents'>
            <Dropdown
              noChevron
              iconTrigger
              selectedContent={<Dots width={15} />}
              dropdownContent={
                <div style={dropdownContentStyle}>
                  <DropdownItem onClick={handleOpenExportPayoutEntriesModal}>
                    Export payout entries
                  </DropdownItem>
                  {payout?.status === 'Complete' && (payout.fiatEntries?.length ?? 0) > 0 &&
                    <DropdownItem onClick={() => {
                      // Open synchronously so the browser treats it as user-initiated,
                      // then point the tab at the generated receipt.
                      const receiptTab = window.open('', '_blank')
                      void (async () => {
                        try {
                          const receipt = await Radom.getPayoutReceipt(payout.id)
                          if (receiptTab) {
                            receiptTab.location.href = receipt.url
                          } else {
                            window.location.assign(receipt.url)
                          }
                        } catch {
                          receiptTab?.close()
                          errorToast('Failed to generate the receipt')
                        }
                      })()
                    }}>
                      Download receipt
                    </DropdownItem>}
                </div>
              }
            />
          </Gate>
        </div>

        <Section>
          {(() => {
            const fundingCurrency = payout.cryptoEntries?.[0]?.fundingCurrency
            if (fundingCurrency) {
              const destinationAmount = (payout.cryptoEntries ?? [])
                .reduce((sum, e) => sum + Number(e.amount), 0)
              const paidAmount = (payout.cryptoEntries ?? []).reduce((sum, e) => {
                const cost = e.hedge ? Number(e.hedge.stablecoinCost) : 0
                return sum + (Number.isFinite(cost) ? cost : 0)
              }, 0)
              const fundingAmount = paidAmount > 0 ? paidAmount : payout.totalAmountUsd
              const fundingLogo = getMethodLogoByTicker(fundingCurrency)
              return (
                <div style={{ ...payoutHeaderStyle, gap: 8 }}>
                  {fundingLogo && <img src={fundingLogo} style={payoutLogoStyle} />}
                  <span>{formatAssetAmount(fundingAmount, fundingCurrency)}</span>
                  <span style={tickerBadgeStyle}>{fundingCurrency}</span>
                  <span style={{ color: '#888' }}>→</span>
                  {paymentMethod && <img src={paymentMethod.logo} style={payoutLogoStyle} />}
                  <span>{formatAssetAmount(destinationAmount, paymentMethod?.ticker)}</span>
                  <span style={tickerBadgeStyle}>{paymentMethod?.ticker}</span>
                </div>
              )
            }
            const cryptoAmount = (payout.cryptoEntries ?? [])
              .reduce((sum, e) => sum + Number(e.amount), 0)
            return (
              <div style={payoutHeaderStyle}>
                {paymentMethod && <img src={paymentMethod.logo} style={payoutLogoStyle} />}
                <span>{formatAssetAmount(isFiatPayout
                  ? (payout.fiatEntries[0]?.amount || payout.totalAmountUsd)
                  : (paymentMethod ? cryptoAmount : payout.totalAmountUsd), undefined, 6)}</span>
                <span style={tickerBadgeStyle}>{isFiatPayout ? payout.fiatEntries[0]?.currency || 'USD' : paymentMethod?.ticker || 'USD'}</span>
              </div>
            )
          })()}

          <SectionContent>
            {/* A hedged payout's conversion fee is inside the rate shown in its cost breakdown. */}
            {!payout.cryptoEntries?.[0]?.fundingCurrency && <SectionItem>
              <SubTitle>
                {isFiatPayout
                  ? 'Estimated fee'
                  : payout.feeSettledAt ? 'Network fee' : 'Network fee (estimated)'}
              </SubTitle>
              <div style={feeRowStyle}>
                {(() => {
                  const fundingCurrency = payout.cryptoEntries?.[0]?.fundingCurrency
                  const feeTicker = isFiatPayout ? 'USDC' : (fundingCurrency || 'USDC')
                  const feeLogo = getMethodLogoByTicker(feeTicker)
                  // Once the batches are sent the fee is settled on what they
                  // cost; until then the estimate is shown, and the reserve
                  // taken at confirm (refunded down to the final fee) next to it.
                  const settled = Boolean(payout.feeSettledAt) &&
                    payout.actualFeeUsd !== undefined && payout.actualFeeUsd !== null
                  const shownFee = settled ? payout.actualFeeUsd : payout.estimatedFeeUsd
                  return <>
                    {feeLogo && <img src={feeLogo} style={feeLogoStyle} />}
                    <span>{formatAssetAmount(shownFee, undefined, 6)} {feeTicker}</span>
                    {!settled && !isFiatPayout && !fundingCurrency && payout.reservedFeeUsd && (
                      <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13 }}>
                        · {formatAssetAmount(payout.reservedFeeUsd, undefined, 2)} USD reserved;
                        {' '}the unused part is refunded once sent
                      </span>
                    )}
                  </>
                })()}
              </div>
            </SectionItem>}

            <SectionItem>
              <SubTitle>Status</SubTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StatusBadge status={payoutBadgeStatus(payout.status)}>
                  {payoutStatusLabel(payout.status, payout.autoConfirm)}
                </StatusBadge>
                {payout.processingStage && payout.status === 'Processing' && (
                  <span style={{ color: RADOM_COLORS.GRAY_DARKER, fontSize: 13 }}>
                    {payout.processingStage}
                    {STAGE_EXPECTATIONS[payout.processingStage] && ` · ${STAGE_EXPECTATIONS[payout.processingStage]}`}
                  </span>
                )}
              </div>
            </SectionItem>

            <SectionItem>
              <SubTitle>Type</SubTitle>
              <span>{isFiatPayout ? 'Fiat' : 'Crypto'}</span>
            </SectionItem>

            <SectionItem>
              <SubTitle>Entries</SubTitle>
              <span>{payout.entryCount}</span>
            </SectionItem>

            <SectionItem>
              <SubTitle>Created</SubTitle>
              <TimeText date={new Date(payout.createdAt)} />
            </SectionItem>

            <SectionItem>
              <SubTitle>Completed</SubTitle>
              {payout.completedAt ? <TimeText date={new Date(payout.completedAt)} /> : <span>-</span>}
            </SectionItem>
          </SectionContent>
        </Section>

        {(payout.cryptoEntries ?? []).some(entry => entry.hedge) && (
          <Section>
            <SubTitle style={ledgerTitleStyle}>Conversion details</SubTitle>

            {(payout.cryptoEntries ?? []).filter(entry => entry.hedge).map(entry => {
              const hedge = entry.hedge
              if (!hedge) return null
              const cryptoTicker = getMethod(entry.network, entry.token).ticker

              return (
                <div key={entry.id} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {(payout.cryptoEntries?.length ?? 0) > 1 && (
                    <span style={ledgerMutedTextStyle}>
                      Entry to {shortAddress(entry.payoutAddress, 8)}
                    </span>
                  )}

                  <SectionContent>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <HedgedPayoutCostBreakdown
                        hedge={hedge}
                        cryptoTicker={cryptoTicker}
                        recipientAmount={entry.amount}
                      />
                    </div>

                    <SectionItem>
                      <SubTitle>Delivery withdrawal</SubTitle>
                      {hedge.deliveryWithdrawalId
                        ? <SecondaryLinkButton
                          to={`/withdrawal/${hedge.deliveryWithdrawalId}`}
                          style={{ minHeight: 32, padding: '0 12px', fontSize: 13 }}
                        >
                          View withdrawal
                        </SecondaryLinkButton>
                        : <span>-</span>}
                    </SectionItem>
                  </SectionContent>
                </div>
              )
            })}
          </Section>
        )}

        {managedBalanceLedger && managedBalanceLedger.length > 0 && (
          <Section>
            <SubTitle style={ledgerTitleStyle}>Ledger</SubTitle>

            {managedBalanceLedger.map((item, idx) => {
              const method = getMethod(item.network, item.token)

              return (
                <SectionContent key={idx}>
                  <SectionItem>
                    <SubTitle>Balance Before</SubTitle>
                    <div style={ledgerRowStyle}>
                      <span>{formatAssetAmount(item.balanceBefore, method.ticker)}</span>
                      <span style={ledgerMutedTextStyle}>
                        {ledgerBalanceLabel(item, method.ticker)}
                      </span>
                    </div>
                  </SectionItem>

                  <SectionItem>
                    <SubTitle>Balance After</SubTitle>
                    <div style={ledgerRowStyle}>
                      <span>{formatAssetAmount(item.balanceAfter, method.ticker)}</span>
                      <span style={ledgerMutedTextStyle}>
                        {ledgerBalanceLabel(item, method.ticker)}
                      </span>
                    </div>
                  </SectionItem>
                </SectionContent>
              )
            })}
          </Section>
        )}

        {/* Crypto entries section */}
        {payout.cryptoEntries && payout.cryptoEntries.length > 0 && paymentMethod && (
          <Section>
            <SubTitle style={ledgerTitleStyle}>Crypto payout entries</SubTitle>

            <Table
              headers={[
                ...(payout.cryptoEntries.some(e => e.externalAccountId)
                  ? [<span key="extAccId">External Account ID</span>]
                  : []),
                <span key="address">Payout Address</span>,
                <span key="amount">Amount</span>,
                <span key="status">Status</span>,
                <span key="beneficiary">Beneficiary Name</span>,
                ...(payout.cryptoEntries.some(e => e.failureReason)
                  ? [<span key="reason">Reason</span>]
                  : []),
                ...(payout.cryptoEntries.some(e => e.externalAccountId)
                  ? [<span key="details">Account Details</span>]
                  : []),
                ...(payout.cryptoEntries.some(e => e.txHash)
                  ? [<span key="tx">Transaction Hash</span>]
                  : [])
              ]}
              isLoading={false}
              emptyPlaceholderText="No crypto entries"
              disableHoverStyle
              items={payout.cryptoEntries.map(entry => {
                const hasExtAcctCol = payout.cryptoEntries.some(e => e.externalAccountId)
                const customer = customers.find(c => c.id === entry.customerId)
                const hasName = Boolean(customer?.name && customer.name.length !== 0 && customer.name !== 'null null')
                const txHash = entry.txHash ?? ''
                const hasReasonCol = payout.cryptoEntries.some(e => e.failureReason)
                const extAcct = entry.externalAccountId
                  ? externalAccounts.get(entry.externalAccountId)
                  : undefined
                return [
                  ...(hasExtAcctCol
                    ? [entry.externalAccountId
                      ? <div key="extAccId" style={payoutAddressCellStyle}>
                        <span>{shortAddress(entry.externalAccountId, 8)}</span>
                        <IconButton style={iconButtonNoPaddingStyle}>
                          <Copy
                            style={copyIconStyle}
                            onClick={e => {
                              e.stopPropagation()
                              void copyToClipboard(entry.externalAccountId || '', 'Copied ID', 'Failed to copy')
                            }}
                          />
                        </IconButton>
                      </div>
                      : <span key="extAccId">-</span>]
                    : []),
                  <div key="address" style={payoutAddressCellStyle}>
                    <span>{shortAddress(entry.payoutAddress, 8)}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        onClick={e => {
                          e.stopPropagation()
                          void copyToClipboard(entry.payoutAddress, 'Copied payout address', 'Failed to copy payout address')
                        }}
                      />
                    </IconButton>
                  </div>,
                  <div key="amount" style={feeRowStyle}>
                    <img src={paymentMethod.logo} style={feeLogoStyle} />
                    <span>{formatAssetAmount(entry.amount, paymentMethod.ticker)}</span>
                  </div>,
                  <StatusBadge key="status" status={payoutBadgeStatus(entry.status)}>
                    {payoutStatusLabel(entry.status)}
                  </StatusBadge>,
                  <span key="beneficiary">{hasName ? customer?.name : '-'}</span>,
                  ...(hasReasonCol
                    ? [<span key="reason">{entry.failureReason || '-'}</span>]
                    : []),
                  ...(hasExtAcctCol
                    ? [extAcct
                      ? <div key="details" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                        <span>{extAcct.accountOwnerName}</span>
                        {extAcct.crypto && (
                          <span style={ledgerMutedTextStyle}>
                            {shortAddress(extAcct.crypto.address, 8)} on {networkName(extAcct.crypto.network)}
                          </span>
                        )}
                      </div>
                      : <span key="details">-</span>]
                    : []),
                  ...(payout.cryptoEntries.some(e => e.txHash)
                    ? [txHash
                      ? <ExternalLinkText
                        key="tx"
                        style={txLinkStyle}
                        onClick={e => e.stopPropagation()}
                        target="_blank"
                        rel="noreferrer"
                        href={formatExplorerUrl(entry.network, 'tx', txHash)}
                      >
                        {shortAddress(txHash, 8)}
                        <LinkExternal stroke={linkExternalIconStyle.stroke} width={linkExternalIconStyle.width} />
                      </ExternalLinkText>
                      : <span key="tx">-</span>]
                    : [])
                ]
              })}
            />
          </Section>
        )}

        {/* Fiat entries section */}
        {payout.fiatEntries && payout.fiatEntries.length > 0 && (
          <Section>
            <SubTitle style={ledgerTitleStyle}>Fiat payout entries</SubTitle>

            <Table
              headers={[
                ...(payout.fiatEntries.some(e => e.externalAccountId)
                  ? [<span key="extAccId">External Account ID</span>]
                  : []),
                <span key="beneficiary">Beneficiary</span>,
                <span key="amount">Amount</span>,
                <span key="currency">Currency</span>,
                <span key="status">Status</span>,
                ...(payout.fiatEntries.some(e => e.failureReason)
                  ? [<span key="reason">Reason</span>]
                  : []),
                ...(payout.fiatEntries.some(e => e.externalAccountId)
                  ? [<span key="details">Account Details</span>]
                  : []),
                ...(payout.fiatEntries.some(e => e.providerReference)
                  ? [<span key="ref">Provider Reference</span>]
                  : [])
              ]}
              isLoading={false}
              emptyPlaceholderText="No fiat entries"
              disableHoverStyle
              items={payout.fiatEntries.map(entry => {
                const hasExtAcctCol = payout.fiatEntries.some(e => e.externalAccountId)
                const hasReasonCol = payout.fiatEntries.some(e => e.failureReason)
                const extAcct = entry.externalAccountId
                  ? externalAccounts.get(entry.externalAccountId)
                  : undefined
                const last4 = extAcct?.iban?.last4 ??
                  extAcct?.gbAccount?.last4 ??
                  extAcct?.account?.last4 ??
                  extAcct?.swift?.last4 ??
                  extAcct?.clabe?.last4
                const bic = extAcct?.iban?.bic ?? extAcct?.swift?.bic
                const country = extAcct?.iban?.country ?? extAcct?.swift?.country
                return [
                  ...(hasExtAcctCol
                    ? [entry.externalAccountId
                      ? <div key="extAccId" style={payoutAddressCellStyle}>
                        <span>{shortAddress(entry.externalAccountId, 8)}</span>
                        <IconButton style={iconButtonNoPaddingStyle}>
                          <Copy
                            style={copyIconStyle}
                            onClick={e => {
                              e.stopPropagation()
                              void copyToClipboard(entry.externalAccountId || '', 'Copied ID', 'Failed to copy')
                            }}
                          />
                        </IconButton>
                      </div>
                      : <span key="extAccId">-</span>]
                    : []),
                  <span key="beneficiary">{entry.beneficiaryName || '-'}</span>,
                  <span key="amount">{formatAssetAmount(entry.amount, entry.currency)} {entry.currency}</span>,
                  <span key="currency">{entry.currency}</span>,
                  <StatusBadge key="status" status={payoutBadgeStatus(entry.status)}>
                    {payoutStatusLabel(entry.status)}
                  </StatusBadge>,
                  ...(hasReasonCol
                    ? [<span key="reason">{entry.failureReason || '-'}</span>]
                    : []),
                  ...(hasExtAcctCol
                    ? [extAcct
                      ? <div key="details" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                        <span>{extAcct.accountOwnerName}</span>
                        {last4 && (
                          <span style={ledgerMutedTextStyle}>Last 4 ····{last4}</span>
                        )}
                        {bic && (
                          <span style={ledgerMutedTextStyle}>BIC {bic}</span>
                        )}
                        {country && (
                          <span style={ledgerMutedTextStyle}>Country {country}</span>
                        )}
                      </div>
                      : <span key="details">-</span>]
                    : []),
                  ...(payout.fiatEntries.some(e => e.providerReference)
                    ? [entry.providerReference
                      ? <div key="ref" style={payoutAddressCellStyle}>
                        <span>{shortAddress(entry.providerReference, 8)}</span>
                        <IconButton style={iconButtonNoPaddingStyle}>
                          <Copy
                            style={copyIconStyle}
                            onClick={e => {
                              e.stopPropagation()
                              void copyToClipboard(entry.providerReference || '', 'Copied reference', 'Failed to copy')
                            }}
                          />
                        </IconButton>
                      </div>
                      : <span key="ref">-</span>]
                    : [])
                ]
              })}
            />
          </Section>
        )}

        {exportPayoutEntriesModalOpen && (
          <Modal
            visible={exportPayoutEntriesModalOpen}
            onClose={handleCloseExportPayoutEntriesModal}
            title="Export payout entries"
          >
            <ExportPayoutEntriesForm id={payout.id} />
          </Modal>
        )}
      </Container>
    )
  }

  return (
    <div>
      <span> Payout not found</span>
    </div>
  )
})

export default Payout
